/**
 * Chart.js charts (via react-chartjs-2) for financial time series and comparisons.
 */
import type { BucketTotals, Summary } from "@shared/finance/ledger.ts";
import { formatPence } from "@shared/money.ts";
import {
  ArcElement,
  BarController,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
  type TooltipItem,
} from "chart.js";
import { Bar, Doughnut } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  LineElement,
  LineController,
  PointElement,
  ArcElement,
  Tooltip,
  Legend,
  Filler,
);

const COLORS = {
  income: "#12a150",
  expense: "#d92d20",
  net: "#1d4ed8",
  finance: "#f79009",
};

const PALETTE = [
  "#1d4ed8",
  "#12a150",
  "#f79009",
  "#d92d20",
  "#7a5af8",
  "#0ba5ec",
  "#ee46bc",
  "#667085",
  "#16b364",
  "#fb6514",
];

const money = (value: number | string) => formatPence(Math.round(Number(value) * 100), { whole: true });

const baseOptions = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: "index" as const, intersect: false },
  plugins: {
    legend: { position: "bottom" as const },
    tooltip: {
      callbacks: { label: (ctx: TooltipItem<"bar">) => `${ctx.dataset.label}: ${money(ctx.parsed.y ?? 0)}` },
    },
  },
  scales: { y: { ticks: { callback: (v: number | string) => money(v) } } },
};

/** Income vs expenses (bars) with net profit/loss (line) per period. */
export function IncomeExpenseChart({
  buckets,
  show = "all",
}: {
  buckets: BucketTotals[];
  show?: "all" | "income" | "expenses" | "net";
}) {
  const labels = buckets.map((b) => b.label);
  const datasets = [];
  if (show === "all" || show === "income")
    datasets.push({
      type: "bar" as const,
      label: "Income",
      data: buckets.map((b) => b.summary.incomePence / 100),
      backgroundColor: COLORS.income,
      borderRadius: 3,
    });
  if (show === "all" || show === "expenses")
    datasets.push({
      type: "bar" as const,
      label: "Expenses",
      data: buckets.map((b) => b.summary.expensesPence / 100),
      backgroundColor: COLORS.expense,
      borderRadius: 3,
    });
  if (show === "all" || show === "net")
    datasets.push({
      type: (show === "net" ? "bar" : "line") as "bar",
      label: "Net profit / loss",
      data: buckets.map((b) => b.summary.netPence / 100),
      borderColor: COLORS.net,
      backgroundColor:
        show === "net"
          ? buckets.map((b) => (b.summary.netPence < 0 ? COLORS.expense : COLORS.net))
          : COLORS.net,
      tension: 0.3,
    });
  return (
    <div className="chart-box" role="img" aria-label="Chart of income, expenses and net profit over time">
      <Bar data={{ labels, datasets }} options={baseOptions} />
    </div>
  );
}

/** Net profit per property, horizontal bars (no forced ranking — ordered by name). */
export function PropertyProfitChart({ groups }: { groups: Array<{ label: string; summary: Summary }> }) {
  const height = Math.max(200, groups.length * 34 + 60);
  return (
    <div className="chart-box" style={{ height }} role="img" aria-label="Chart of profit by property">
      <Bar
        data={{
          labels: groups.map((g) => g.label),
          datasets: [
            {
              label: "Income",
              data: groups.map((g) => g.summary.incomePence / 100),
              backgroundColor: COLORS.income,
            },
            {
              label: "Expenses",
              data: groups.map((g) => g.summary.expensesPence / 100),
              backgroundColor: COLORS.expense,
            },
            { label: "Net", data: groups.map((g) => g.summary.netPence / 100), backgroundColor: COLORS.net },
          ],
        }}
        options={{
          ...baseOptions,
          indexAxis: "y" as const,
          plugins: {
            ...baseOptions.plugins,
            tooltip: {
              callbacks: {
                label: (ctx: TooltipItem<"bar">) => `${ctx.dataset.label}: ${money(ctx.parsed.x ?? 0)}`,
              },
            },
          },
          scales: { x: { ticks: { callback: (v: number | string) => money(v) } } },
        }}
      />
    </div>
  );
}

export function BreakdownChart({ items }: { items: Array<{ label: string; pence: number }> }) {
  const data = items.filter((i) => i.pence > 0);
  if (data.length === 0) return <p className="text-muted small mb-0">Nothing to show for this period.</p>;
  return (
    <div className="chart-box" style={{ height: 260 }} role="img" aria-label="Breakdown chart">
      <Doughnut
        data={{
          labels: data.map((d) => d.label),
          datasets: [
            {
              data: data.map((d) => d.pence / 100),
              backgroundColor: data.map((_, i) => PALETTE[i % PALETTE.length]),
            },
          ],
        }}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { position: "right" as const },
            tooltip: {
              callbacks: {
                label: (ctx: { label: string; parsed: number }) => `${ctx.label}: ${money(ctx.parsed)}`,
              },
            },
          },
        }}
      />
    </div>
  );
}
